import React from "react";
import { View, Text } from "react-native";
import { parseFirestoreDate } from "../utils/formatting";

const dividerStyle = { backgroundColor: "#FBEAE9" };
const lineItemColor = "#5C6B68";
const lineItemColorMuted = "#8A9794";

/**
 * Previous Order Summary – fixed layout with all lines always shown.
 * Uses originalBooking data. Background: red tint.
 */
const PreviousOrderSummary = ({
  booking,
  styles,
  product1Catalog = [],
  product2Catalog = [],
  product1Title = "Bicycles",
  product2Title = "Add-Ons",
  settingsObject,
  productCostCents = 0,
  formatDollarsAsUSDWithCents,
  centsToDollars,
}) => {
  if (!booking) return null;

  const formatDate = (dateField) => {
    const d = parseFirestoreDate(dateField);
    if (!d) return "N/A";
    try {
      const dayAbbr = d.toLocaleDateString("en-US", { weekday: "short" });
      const monthAbbr = d.toLocaleDateString("en-US", { month: "short" });
      const day = d.getDate();
      const year = d.getFullYear().toString().slice(-2);
      return `${dayAbbr} ${monthAbbr} ${day}, '${year}`;
    } catch {
      return "N/A";
    }
  };

  const startDate = booking.startDate ? parseFirestoreDate(booking.startDate) : null;
  const endDate = booking.endDate ? parseFirestoreDate(booking.endDate) : null;
  // Round, not ceil — keeps the displayed day count equal to what the engine charges.
  const totalDays = startDate && endDate ? Math.round((endDate - startDate) / (1000 * 60 * 60 * 24)) || 1 : 0;

  const product1 = booking.product1 || [];
  const product2 = booking.product2 || [];
  const totalBikes = product1.reduce((sum, p) => sum + (p.quantity || 0), 0);
  const deliveryCents = Number(booking.deliveryCostCents) || 0;
  const pickupCents = Number(booking.pickupCostCents) || 0;
  const durationDiscountCents = Number(booking.durationDiscount?.discountCents) || 0;
  const durationDiscountPercent = Number(booking.durationDiscount?.discountPercent) || 0;
  const transportDiscountCents = Number(booking.transportationDiscount?.discountCents) || 0;
  const transportDiscountPercent = Number(booking.transportationDiscount?.discountPercent) || 0;

  const subtotalWithoutDiscountCents = productCostCents + deliveryCents + pickupCents;
  const subtotalWithDiscountCents = subtotalWithoutDiscountCents - transportDiscountCents - durationDiscountCents;
  const platformFeeCents = Number(settingsObject?.bookingPlatform?.perBookingFee) || 0;
  const salesTaxPercent = Number(settingsObject?.salesTax?.percent) || 0;
  const taxCents = Math.round((subtotalWithDiscountCents + platformFeeCents) * (salesTaxPercent / 100));
  const cardFeePercent = Number(settingsObject?.cardProcessingFee?.percent) || 0;
  const cardFeeForPurchases = settingsObject?.cardProcessingFee?.forPurchases;
  const cardFeeCents = cardFeeForPurchases && cardFeePercent > 0
    ? Math.round((subtotalWithDiscountCents + platformFeeCents + taxCents) * (cardFeePercent / 100))
    : 0;
  const finalTotalCents = subtotalWithDiscountCents + platformFeeCents + taxCents + cardFeeCents;

  const formatCents = (cents) => formatDollarsAsUSDWithCents(centsToDollars(cents));

  return (
    <View style={[styles.section, { backgroundColor: "#FBEAE9", padding: 0 }]}>
      {/* 1. Rental dates */}
      <View style={[styles.summaryRow, { borderBottomColor: "#FBEAE9" }]}>
        <Text style={styles.summarySectionTitle}>Rental Dates:</Text>
        {startDate && endDate ? (
          <Text style={styles.summaryValue}>
            <Text style={{ color: "#1F8A5B", fontWeight: "600" }}>{formatDate(booking.startDate)}</Text>
            {" → "}
            <Text style={{ color: "#0E7C6B", fontWeight: "600" }}>{formatDate(booking.endDate)}</Text>
          </Text>
        ) : (
          <Text style={[styles.summaryValue, { color: "#8A9794" }]}>Not selected</Text>
        )}
      </View>

      {/* 2. Products rented */}
      <Text style={styles.summarySectionTitle}>Products Rented</Text>
      {product1.length > 0 && product1.map((item, idx) => {
        const catalog = product1Catalog.find((p) => p.id === item.id);
        const name = catalog?.name || item.name || "Item";
        const priceCents = catalog?.price || 0;
        const lineTotal = priceCents * (item.quantity || 0) * totalDays;
        if ((item.quantity || 0) === 0) return null;
        const muted = !lineTotal;
        return (
          <View key={`p1-${idx}`} style={styles.summaryItemRow}>
            <Text style={[styles.summaryItemText, { color: muted ? lineItemColorMuted : lineItemColor }]}>{item.quantity}x {name}</Text>
            <Text style={[styles.summaryItemPrice, { color: muted ? lineItemColorMuted : lineItemColor }]}>{formatCents(lineTotal)}</Text>
          </View>
        );
      })}
      {product2.length > 0 && product2.map((item, idx) => {
        const catalog = product2Catalog.find((p) => p.id === item.id);
        const name = catalog?.name || item.name || "Add-on";
        const priceCents = catalog?.price || 0;
        const opt = catalog?.option || "per-booking";
        let lineTotal = 0;
        if (opt === "per-booking") lineTotal = priceCents * (item.quantity || 0);
        else if (opt === "per-bike") lineTotal = priceCents * (item.quantity || 0) * totalBikes;
        else lineTotal = priceCents * (item.quantity || 0) * totalDays;
        if (lineTotal === 0) return null;
        const muted = !lineTotal;
        const label = opt === "per-bike"
          ? `${name} (×${totalBikes} bike${totalBikes !== 1 ? "s" : ""})`
          : `${item.quantity}x ${name}`;
        return (
          <View key={`p2-${idx}`} style={styles.summaryItemRow}>
            <Text style={[styles.summaryItemText, { color: muted ? lineItemColorMuted : lineItemColor }]}>{label}</Text>
            <Text style={[styles.summaryItemPrice, { color: muted ? lineItemColorMuted : lineItemColor }]}>{formatCents(lineTotal)}</Text>
          </View>
        );
      })}
      {product1.length === 0 && product2.length === 0 && (
        <View style={styles.summaryItemRow}>
          <Text style={[styles.summaryItemText, { color: "#8A9794" }]}>None</Text>
          <Text style={styles.summaryItemPrice}>{formatCents(0)}</Text>
        </View>
      )}

      {/* 3. Equipment subtotal */}
      <View style={styles.summaryItemRow}>
        <Text style={[styles.summaryItemText, { fontWeight: "600", fontSize: 15 }]}>Equipment Subtotal</Text>
        <Text style={[styles.summaryItemPrice, { fontWeight: "600", fontSize: 15 }]}>{formatCents(productCostCents)}</Text>
      </View>

      <View style={[styles.summaryItemDivider, dividerStyle]} />

      <Text style={[styles.summarySectionTitle, { marginTop: 0 }]}>Transportation</Text>
      {/* 4. Transportation charges – always show both lines */}
      <View style={styles.summaryItemRow}>
        <Text style={[styles.summaryItemText, { color: !deliveryCents ? lineItemColorMuted : lineItemColor }]}>Delivery{booking.numMiles != null ? ` (${Number(booking.numMiles).toFixed(1)} mi)` : ""}</Text>
        <Text style={[styles.summaryItemPrice, { color: !deliveryCents ? lineItemColorMuted : lineItemColor }]}>{formatCents(deliveryCents)}</Text>
      </View>
      <View style={styles.summaryItemRow}>
        <Text style={[styles.summaryItemText, { color: !pickupCents ? lineItemColorMuted : lineItemColor }]}>Pickup</Text>
        <Text style={[styles.summaryItemPrice, { color: !pickupCents ? lineItemColorMuted : lineItemColor }]}>{formatCents(pickupCents)}</Text>
      </View>
      <View style={styles.summaryItemRow}>
        <Text style={[styles.summaryItemText, { fontWeight: "600", fontSize: 15 }]}>Subtotal (without discount)</Text>
        <Text style={[styles.summaryItemPrice, { fontWeight: "600", fontSize: 15 }]}>{formatCents(subtotalWithoutDiscountCents)}</Text>
      </View>

      <View style={[styles.summaryItemDivider, dividerStyle]} />

      <Text style={[styles.summarySectionTitle, { marginTop: 0 }]}>Discounts</Text>
      {/* 6. Transportation discount – always show (e.g. $0.00) */}
      <View style={styles.summaryItemRow}>
        <Text style={[styles.summaryItemText, styles.discountText || {}, { color: !transportDiscountCents ? lineItemColorMuted : lineItemColor }]}>
          Transportation Discount{transportDiscountPercent ? ` (${transportDiscountPercent}%)` : ""}
        </Text>
        <Text style={[styles.summaryItemPrice, styles.discountText || {}, { color: !transportDiscountCents ? lineItemColorMuted : lineItemColor }]}>
          -{formatCents(transportDiscountCents)}
        </Text>
      </View>
      {/* 7. Duration discount – always show (e.g. $0.00) */}
      <View style={styles.summaryItemRow}>
        <Text style={[styles.summaryItemText, styles.discountText || {}, { color: !durationDiscountCents ? lineItemColorMuted : lineItemColor }]}>
          Duration Discount{durationDiscountPercent ? ` (${durationDiscountPercent}%)` : ""}
        </Text>
        <Text style={[styles.summaryItemPrice, styles.discountText || {}, { color: !durationDiscountCents ? lineItemColorMuted : lineItemColor }]}>
          -{formatCents(durationDiscountCents)}
        </Text>
      </View>

      {/* 8. Subtotal with discount */}
      <View style={styles.summaryItemRow}>
        <Text style={[styles.summaryItemText, { fontWeight: "600", fontSize: 15 }]}>Subtotal (with discount)</Text>
        <Text style={[styles.summaryItemPrice, { fontWeight: "600", fontSize: 15 }]}>{formatCents(subtotalWithDiscountCents)}</Text>
      </View>

      <View style={[styles.summaryItemDivider, dividerStyle]} />

      <Text style={[styles.summarySectionTitle, { marginTop: 0 }]}>Fees</Text>
      {/* 9. Platform fee */}
      <View style={styles.summaryItemRow}>
        <Text style={[styles.summaryItemText, { color: !platformFeeCents ? lineItemColorMuted : lineItemColor }]}>Platform Fee</Text>
        <Text style={[styles.summaryItemPrice, { color: !platformFeeCents ? lineItemColorMuted : lineItemColor }]}>{formatCents(platformFeeCents)}</Text>
      </View>
      {/* 10. Card processing fee */}
      <View style={styles.summaryItemRow}>
        <Text style={[styles.summaryItemText, { color: !cardFeeCents ? lineItemColorMuted : lineItemColor }]}>Card Processing Fee</Text>
        <Text style={[styles.summaryItemPrice, { color: !cardFeeCents ? lineItemColorMuted : lineItemColor }]}>{formatCents(cardFeeCents)}</Text>
      </View>
      {/* 11. Sales tax */}
      <View style={styles.summaryItemRow}>
        <Text style={[styles.summaryItemText, { color: !taxCents ? lineItemColorMuted : lineItemColor }]}>Sales Tax{salesTaxPercent ? ` (${salesTaxPercent}%)` : ""}</Text>
        <Text style={[styles.summaryItemPrice, { color: !taxCents ? lineItemColorMuted : lineItemColor }]}>{formatCents(taxCents)}</Text>
      </View>

      {/* 12. Total */}
      <View style={[styles.summaryTotalRow, { borderTopColor: "#C4453D" }]}>
        <Text style={styles.summaryTotalLabel}>Total</Text>
        <Text style={styles.summaryTotalValue}>{formatCents(finalTotalCents)}</Text>
      </View>
    </View>
  );
};

export default PreviousOrderSummary;
